// components/CrossRestaurantDialog.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  Restaurant as RestaurantIcon,
  DeleteSweep as ClearIcon,
  Cancel as CancelIcon,
  ShoppingCart as CartIcon
} from '@mui/icons-material';

const CrossRestaurantDialog = ({
  open,
  onClose,
  onConfirmClear,
  currentCartRestaurant,
  newRestaurant,
  language
}) => {
  const isArabic = language === 'ar';

  const handleClearAndAdd = () => {
    onConfirmClear();
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#0D0D0D',
          border: '2px solid #FFD700',
          borderRadius: '24px',
          boxShadow: '0 20px 40px rgba(255, 215, 0, 0.25)',
          overflow: 'hidden',
          position: 'relative'
        }
      }}
    >
      {/* Decorative top border */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: 'linear-gradient(90deg, #FFD700, #FFE55C, #FFD700)'
      }} />

      {/* Warning Icon Header */}
      <Box sx={{ 
        textAlign: 'center', 
        pt: 3, 
        pb: 1,
        background: 'linear-gradient(180deg, rgba(255, 215, 0, 0.1) 0%, transparent 100%)'
      }}>
        <Box sx={{
          width: 70,
          height: 70,
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 61, 0, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          border: '2px solid #FF3D00',
          animation: 'pulse 2s infinite'
        }}>
          <WarningIcon sx={{ fontSize: 40, color: '#FF3D00' }} />
        </Box>
      </Box>

      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        pt: 2
      }}>
        <Typography variant="h5" sx={{ 
          color: '#FFD700', 
          fontWeight: 800,
          mb: 1
        }}>
          {isArabic ? 'تحذير: مطعم مختلف' : 'Warning: Different Restaurant'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ 
          color: '#FFFFFF', 
          textAlign: 'center',
          mb: 3,
          lineHeight: 1.6
        }}>
          {isArabic 
            ? 'لديك بالفعل منتجات في سلة التسوق من مطعم آخر. هل تريد مسح السلة الحالية وإضافة منتجات من المطعم الجديد؟'
            : 'You already have items in your cart from another restaurant. Do you want to clear your current cart and add items from the new restaurant?'
          }
        </Typography>

        {/* Current Restaurant Card */}
        <Box sx={{ 
          mb: 2,
          p: 2,
          backgroundColor: '#1A1A1A',
          borderRadius: '16px',
          border: '1px solid #2D2D2D'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <RestaurantIcon sx={{ color: '#FFD700' }} />
            <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 600 }}>
              {isArabic ? 'المطعم الحالي' : 'Current Restaurant'}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
            {isArabic ? currentCartRestaurant?.name : currentCartRestaurant?.name_en || currentCartRestaurant?.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip 
              icon={<CartIcon sx={{ fontSize: 16 }} />}
              label={isArabic ? 'يوجد منتجات في السلة' : 'Items in cart'}
              size="small"
              sx={{ backgroundColor: '#FFD700', color: '#000000', fontWeight: 600 }}
            />
          </Box>
        </Box>

        {/* Arrow Icon */}
        <Box sx={{ textAlign: 'center', my: 1 }}>
          <Typography variant="h6" sx={{ color: '#B3B3B3' }}>↓</Typography>
        </Box>

        {/* New Restaurant Card */}
        <Box sx={{ 
          p: 2,
          backgroundColor: '#1A1A1A',
          borderRadius: '16px',
          border: '1px solid #2D2D2D'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <RestaurantIcon sx={{ color: '#FFD700' }} />
            <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 600 }}>
              {isArabic ? 'المطعم الجديد' : 'New Restaurant'}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
            {isArabic ? newRestaurant?.name : newRestaurant?.name_en || newRestaurant?.name}
          </Typography>
        </Box>

        {/* Info Note */}
        <Box sx={{ 
          mt: 3,
          p: 2,
          backgroundColor: 'rgba(255, 215, 0, 0.05)',
          borderRadius: '12px',
          border: '1px solid #2D2D2D'
        }}>
          <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', textAlign: 'center' }}>
            {isArabic 
              ? '⚠️ لا يمكنك إضافة منتجات من مطاعم مختلفة في نفس الطلب'
              : '⚠️ You cannot add items from different restaurants in the same order'
            }
          </Typography>
        </Box>
      </DialogContent>

      <Divider sx={{ backgroundColor: '#2D2D2D', my: 1 }} />

      <DialogActions sx={{ 
        p: 3,
        justifyContent: 'space-between',
        gap: 2
      }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          fullWidth
          startIcon={<CancelIcon />}
          sx={{
            borderColor: '#2D2D2D',
            color: '#B3B3B3',
            py: 1.5,
            borderRadius: '12px',
            '&:hover': {
              borderColor: '#FFD700',
              color: '#FFD700',
              backgroundColor: 'rgba(255, 215, 0, 0.08)'
            }
          }}
        >
          {isArabic ? 'إلغاء' : 'Cancel'}
        </Button>

        <Button
          onClick={handleClearAndAdd}
          variant="contained"
          fullWidth
          startIcon={<ClearIcon />}
          sx={{
            backgroundColor: '#FFD700',
            color: '#000000',
            fontWeight: 800,
            py: 1.5,
            borderRadius: '12px',
            '&:hover': {
              backgroundColor: '#FFE55C',
              transform: 'scale(1.02)',
              boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)'
            }
          }}
        >
          {isArabic ? 'مسح' : 'Clear'}
        </Button>
      </DialogActions>

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </Dialog>
  );
};

export default CrossRestaurantDialog;